import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

const springConfig = {
  damping: 25,
  stiffness: 120,
  mass: 0.8,
};

export default function TiltedCard({
  children,
  captionText = "",
  containerHeight = "100%",
  containerWidth = "100%",
  showTooltip = false,
}) {
  const ref = useRef(null);
  const frame = useRef(null);

  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const smoothX = useSpring(x, springConfig);
  const smoothY = useSpring(y, springConfig);

  const rotateX = useTransform(smoothY, [-0.5, 0.5], [4, -4]);
  const rotateY = useTransform(smoothX, [-0.5, 0.5], [-4, 4]);

  const scale = useSpring(1, springConfig);

  const tooltipOpacity = useSpring(0, {
    damping: 20,
    stiffness: 100,
  });

  const tooltipX = useTransform(smoothX, [-0.5, 0.5], [-10, 10]);

  const updatePointer = (clientX, clientY) => {
    if (!ref.current) return;

    const rect = ref.current.getBoundingClientRect();

    const mouseX = (clientX - rect.left) / rect.width - 0.5;
    const mouseY = (clientY - rect.top) / rect.height - 0.5;

    x.set(mouseX);
    y.set(mouseY);
  };

  const handleMouseMove = (e) => {
    if (frame.current) cancelAnimationFrame(frame.current);

    frame.current = requestAnimationFrame(() => {
      updatePointer(e.clientX, e.clientY);
    });
  };

  const handleMouseEnter = () => {
    scale.set(1.02);
    tooltipOpacity.set(1);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
    scale.set(1);
    tooltipOpacity.set(0);
  };

  return (
    <figure
      ref={ref}
      className="relative flex items-center justify-center group"
      style={{
        height: containerHeight,
        width: containerWidth,
        perspective: "1000px",
      }}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.div
        className="relative h-full w-full"
        style={{
          rotateX,
          rotateY,
          scale,
          transformStyle: "preserve-3d",
          willChange: "transform",
          transform: "translateZ(0)",
        }}
      >
        <div
          className="h-full w-full"
          style={{
            transform: "translateZ(0)",
            willChange: "transform",
          }}
        >
          {children}
        </div>
      </motion.div>

      {showTooltip && captionText && (
        <motion.figcaption
          className="pointer-events-none absolute z-50 rounded-full border border-white/10 bg-black/80 px-3 py-1 text-xs font-medium text-white backdrop-blur-md"
          style={{
            opacity: tooltipOpacity,
            x: tooltipX,
            y: 40,
          }}
        >
          {captionText}
        </motion.figcaption>
      )}
    </figure>
  );
}
